<template>
    <glass-dialog :value="value" title="Como funciona esta área" :max-width="760" @input="$emit('input', $event)">
        <p class="guide-intro">
            Aqui você cria artigos para o blog do seu site — eles ajudam novos pacientes a
            encontrarem você no Google. Existem dois jeitos de usar:
        </p>

        <tab-switcher v-model="flow" :items="flowTabs" aria-label="Fluxos" class="guide-tabs" />

        <div v-if="flow === 'manual'" class="guide-steps">
            <div v-for="(step, i) in manualSteps" :key="step.title" class="guide-step">
                <div class="guide-step__medal">
                    <span class="guide-step__number">{{ i + 1 }}</span>
                    <v-icon size="26" color="primary">{{ step.icon }}</v-icon>
                </div>
                <div class="guide-step__body">
                    <span class="guide-step__title">{{ step.title }}</span>
                    <span class="guide-step__desc">{{ step.description }}</span>
                </div>
            </div>
        </div>

        <div v-else class="guide-steps">
            <div v-for="(step, i) in autoSteps" :key="step.title" class="guide-step">
                <div class="guide-step__medal">
                    <span class="guide-step__number">{{ i + 1 }}</span>
                    <v-icon size="26" color="primary">{{ step.icon }}</v-icon>
                </div>
                <div class="guide-step__body">
                    <span class="guide-step__title">{{ step.title }}</span>
                    <span class="guide-step__desc">{{ step.description }}</span>
                </div>
            </div>
        </div>

        <div ref="creditsBox" class="guide-credits" :class="{ 'guide-credits--highlight': highlightCredits }">
            <info-notice
                variant="primary"
                icon="mdi-flash"
                title="O que são créditos?"
                description="Cada artigo criado usa 1 crédito do seu plano. Receber ideias de temas é grátis. Seus créditos se renovam junto com a sua assinatura."
            />
        </div>

        <template #actions>
            <v-btn color="primary" @click="$emit('input', false)">Entendi</v-btn>
        </template>
    </glass-dialog>
</template>

<script>
export default {
    name: 'ArtigosComoFunciona',
    props: {
        value: { type: Boolean, default: false },
        // 'credits' rola até a explicação de créditos; 'manual'/'auto' abre no fluxo correspondente
        section: { type: String, default: null },
    },
    data() {
        return {
            flow: 'manual',
            highlightCredits: false,
            flowTabs: [
                { label: 'Criar um artigo', icon: 'mdi-creation', value: 'manual' },
                { label: 'Publicação automática', icon: 'mdi-calendar-sync-outline', value: 'auto' },
            ],
            manualSteps: [
                {
                    icon: 'mdi-lightbulb-on-outline',
                    title: 'Escolha um tema',
                    description: 'Sugerimos ideias com base no seu perfil e na sua especialidade. Você também pode inventar o seu.',
                },
                {
                    icon: 'mdi-magnify',
                    title: 'Nós preparamos tudo',
                    description: 'Olhamos o que já funciona no Google sobre o tema e montamos o plano do artigo. Você só confirma.',
                },
                {
                    icon: 'mdi-file-document-edit-outline',
                    title: 'Leia e ajuste',
                    description: 'A inteligência artificial escreve o artigo completo. Você pode mudar qualquer parte.',
                },
                {
                    icon: 'mdi-send-check-outline',
                    title: 'Publique no seu site',
                    description: 'Escolha uma imagem e publique na hora — ou marque uma data.',
                },
            ],
            autoSteps: [
                {
                    icon: 'mdi-check-circle-outline',
                    title: 'Marque os temas que você quer',
                    description: "Na área Criar artigo, toque em 'Quero usar' nas ideias que valem a pena.",
                },
                {
                    icon: 'mdi-calendar-week',
                    title: 'Escolha os dias',
                    description: '2 ou 8 artigos por mês, nos dias e horários que você preferir.',
                },
                {
                    icon: 'mdi-email-check-outline',
                    title: 'Aprove por e-mail',
                    description: 'Dois dias antes de cada data, escrevemos o artigo e enviamos para você ler. Um toque e está aprovado.',
                },
                {
                    icon: 'mdi-rocket-launch-outline',
                    title: 'Publicamos para você',
                    description: 'Na data marcada, o artigo entra no seu site sozinho.',
                },
            ],
        }
    },
    watch: {
        value(open) {
            if (!open) return
            if (this.section === 'manual' || this.section === 'auto') {
                this.flow = this.section
            }
            if (this.section === 'credits') {
                this.$nextTick(() => {
                    this.$refs.creditsBox?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    this.highlightCredits = true
                    setTimeout(() => { this.highlightCredits = false }, 2400)
                })
            }
        },
    },
}
</script>

<style scoped>
.guide-intro {
    font-size: 14px;
    line-height: 1.6;
    color: var(--color-on-surface-variant, #5b5b64);
    margin: 0 0 4px;
}

.guide-tabs {
    margin-bottom: 4px;
}

.guide-steps {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 4px 2px 14px;
}

.guide-step {
    display: flex;
    align-items: flex-start;
    gap: 16px;
}

.guide-step__medal {
    position: relative;
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background: rgba(112, 42, 225, 0.09);
    border: 1px solid rgba(112, 42, 225, 0.18);
}

.guide-step__number {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background: var(--color-primary, #702ae1);
    color: #fff;
    font-size: 11px;
    font-weight: 800;
}

.guide-step__body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-top: 4px;
}

.guide-step__title {
    font-size: 14.5px;
    font-weight: 800;
    color: var(--color-on-surface, #2d2e37);
}

.guide-step__desc {
    font-size: 13px;
    line-height: 1.55;
    color: var(--color-on-surface-variant, #5b5b64);
}

.guide-credits {
    border-radius: var(--radius-sm, 12px);
    transition: box-shadow 400ms ease;
}

.guide-credits--highlight {
    box-shadow: 0 0 0 3px rgba(112, 42, 225, 0.35);
}
</style>
